import { type FC, type HTMLAttributeAnchorTarget, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { ProductImage } from './Carousel/ProductImage.tsx';
import { ProductPriceBadge } from './Details/ProductPriceBadge.tsx';
import {
    getBackgroundColor,
    type ImageBackgroundColor,
    type OnClick,
    type SrcSet,
    type TileSize,
} from './constants.ts';

import dibsCss from 'dibs-css';
import styles from './styles/styles.scss';

import { type BuyerProductImageWithPrice_item$key } from './__generated__/BuyerProductImageWithPrice_item.graphql.ts';

const itemFragment = graphql`
    fragment BuyerProductImageWithPrice_item on Item
    @argumentDefinitions(
        isTrade: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        priceBookName: { type: "String" }
    ) {
        ...ProductImage_item
        productPhotos: photos(limit: 1) {
            ...ProductImage_photo
        }
        ...ProductPriceBadge_item
            @arguments(
                isTrade: $isTrade
                pageDisplayEnum: $pageDisplayEnum
                priceBookName: $priceBookName
            )
    }
`;

type Props = {
    item: BuyerProductImageWithPrice_item$key | null | undefined;
    currency: string;
    buyerHost?: string;
    dataTn?: string;
    imageBackgroundColor?: ImageBackgroundColor;
    disableLinks?: boolean;
    imageLoadVerticalOffset?: number;
    imagePath?: string | null;
    index: number;
    itemsPerRow: number;
    linkTarget?: HTMLAttributeAnchorTarget;
    onClick?: OnClick;
    onContentLoaded?: () => void;
    size: TileSize;
    srcSet?: SrcSet;
    favoritesComponent?: ReactElement;
};

export const BuyerProductImageWithPrice: FC<Props> = ({
    item: itemRef,
    currency,
    buyerHost,
    dataTn,
    disableLinks,
    imageBackgroundColor,
    imageLoadVerticalOffset,
    imagePath,
    index,
    itemsPerRow,
    linkTarget,
    onClick,
    onContentLoaded,
    size,
    srcSet,
    favoritesComponent,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const photos = item?.productPhotos?.[0];
    const backgroundColor = getBackgroundColor(imageBackgroundColor);

    return (
        <article
            className={classnames(
                dibsCss.relative,
                dibsCss.flex,
                dibsCss.flexCol,
                dibsCss.wFull,
                dibsCss.minW0,
                backgroundColor
            )}
        >
            <ProductImage
                item={item}
                photo={photos}
                buyerHost={buyerHost}
                dataTn={dataTn}
                disableLinks={disableLinks}
                imageBackgroundColor={imageBackgroundColor}
                imageDraggable={false}
                imageLoadVerticalOffset={imageLoadVerticalOffset}
                imagePath={imagePath}
                isEagerImageLoadEnabled={index < itemsPerRow}
                hasVideo={false}
                linkTarget={linkTarget}
                onClick={onClick}
                onContentLoaded={onContentLoaded}
                size={size}
                srcSet={srcSet}
            />
            {favoritesComponent}
            <div
                className={classnames(
                    styles.priceBackground,
                    dibsCss.frostedBackground,
                    dibsCss.absolute,
                    dibsCss.bottom0,
                    dibsCss.mxXxsmall,
                    dibsCss.mbXxsmall,
                    dibsCss.pxXxsmall,
                    dibsCss.mt0,
                    dibsCss.wAuto,
                    dibsCss.pointerEventsNone
                )}
            >
                {item && <ProductPriceBadge currency={currency} item={item} />}
            </div>
        </article>
    );
};
